import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define the shape of the Context Payload that will be sent to the LLM
export interface ScreenContext {
    currentModule: string | null;
    currentView: string | null;
    activeRecordId: string | number | null;
    activeFilters: Record<string, any> | null;
}

interface AICopilotContextType {
    screenContext: ScreenContext;
    setScreenContext: React.Dispatch<React.SetStateAction<ScreenContext>>;
    updateContext: (partial: Partial<ScreenContext>) => void;
}

const defaultContext: ScreenContext = {
    currentModule: null,
    currentView: null,
    activeRecordId: null,
    activeFilters: null,
};

const AICopilotContext = createContext<AICopilotContextType | undefined>(undefined);

export const AICopilotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [screenContext, setScreenContext] = useState<ScreenContext>(defaultContext);

    const updateContext = (partial: Partial<ScreenContext>) => {
        setScreenContext(prev => ({ ...prev, ...partial }));
    };

    return (
        <AICopilotContext.Provider value={{ screenContext, setScreenContext, updateContext }}>
            {children}
        </AICopilotContext.Provider>
    );
};

export const useAICopilotContext = () => {
    const context = useContext(AICopilotContext);
    if (!context) {
        throw new Error('useAICopilotContext must be used within an AICopilotProvider');
    }
    return context;
};

/**
 * A handy hook for pages to declare their context on mount and clear it on unmount.
 */
export const useScreenContextSetter = (contextPayload: Partial<ScreenContext>) => {
    const { updateContext } = useAICopilotContext();

    useEffect(() => {
        updateContext(contextPayload);

        // Optional: cleanup on unmount if we wanted to clear it, but often we just 
        // let the next page overwrite it. For safety, we can clear the recordId.
        return () => {
            updateContext({ activeRecordId: null, activeFilters: null });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(contextPayload)]);
};
